import { useMutation, useQuery } from '@tanstack/react-query'
import axios, { AxiosResponse } from 'axios'
import { categoriesQueryKey, getCategories } from 'containers/categories/queries'
import { getItems, itemsQueryKey } from 'containers/items/queries'
import { getQuantityUnits, quantityUnitsQueryKey } from 'containers/quantityUnits/queries'
import {
    AddItemToRecipePayload,
    ConfirmImportedRecipePayload,
    EditRecipePayload,
    NewRecipe,
    Recipe,
    RecipeItem,
    SortRecipeIngredientsPayload,
    UpdateRecipeItemQuantityPayload
} from 'containers/recipes/types'
import { fireErrorNotification, queryClient } from 'utils/queryClient'
import { QueryKeySet } from 'utils/queryClient/keyFactory'
import type { MutationResponse, QueryResponse } from 'utils/queryClient/types'
import { ApiQueryParams } from 'utils/routing/useUrlFilter'

const recipesKeySet = new QueryKeySet('Recipe')

/***** Get recipes *****/
const getRecipes = (search?: string): Promise<QueryResponse<{ recipes: Recipe[] }>> => {
    return axios.get(`/recipe${search ?? ''}`)
}
export type GetRecipesReturnType = ReturnType<typeof getRecipes>
export const recipesQueryKey = recipesKeySet.many

export function useGetRecipesQuery(queryParams?: ApiQueryParams) {
    function convertFiltersToSearchString() {
        if ((queryParams?.filters ?? []).length <= 0) {
            return ''
        }

        const searchParams = new URLSearchParams()

        queryParams?.filters.forEach((filter) => {
            if (filter.content.type === '$in') {
                searchParams.set(`filter-${filter.name}`, `$in:${filter.content.value.join(',')}`)
            }
        })

        return '?' + searchParams.toString()
    }

    const searchString = convertFiltersToSearchString()

    return useQuery({
        queryKey: [...recipesQueryKey(), searchString],
        queryFn: () => getRecipes(searchString),
        select: (res) => res.data.recipes
    })
}

export function prefetchGetRecipesQuery() {
    queryClient.prefetchQuery({ queryKey: recipesQueryKey(), queryFn: () => getRecipes() })
}

/***** Create recipe *****/
const createRecipe = (newRecipe: NewRecipe): Promise<MutationResponse<{ recipe_id: number }>> => axios.post('/recipe', newRecipe)

export function useCreateRecipeMutation() {
    return useMutation({
        mutationFn: createRecipe,
        onSuccess(res) {
            queryClient.invalidateQueries(recipesQueryKey())
            prefetchSingleRecipeQuery(res.data?.recipe_id.toString() || '')
        }
    })
}

/***** Delete recipe *****/
const deleteRecipe = (id: string): Promise<MutationResponse> => axios.delete(`/recipe/${id}`)

export function useDeleteRecipeMutation() {
    return useMutation({
        mutationFn: deleteRecipe
    })
}

/***** Get single recipe *****/
const getSingleRecipe = (id: string): Promise<QueryResponse<{ recipe: Recipe }>> => axios.get(`/recipe/${id}`)
export const singleRecipeQueryKey = recipesKeySet.one

export function useGetSingleRecipeQuery(id: string) {
    return useQuery({
        queryKey: singleRecipeQueryKey(id),
        queryFn: () => getSingleRecipe(id),
        select: (res) => res.data.recipe
    })
}

export function prefetchSingleRecipeQuery(recipeId: string) {
    queryClient.prefetchQuery({
        queryKey: singleRecipeQueryKey(recipeId),
        queryFn: () => getSingleRecipe(recipeId)
    })
}

/***** Add item to recipe *****/
const addItemToRecipe = async ({
    recipeId,
    itemName,
    existingCategoryId,
    newCategory,
    quantity,
    quantityUnitId
}: AddItemToRecipePayload): Promise<MutationResponse> => {
    const body: { item_name: string; category_id?: string; quantity: number; quantity_unit_id?: number } = { item_name: itemName, quantity }

    if (newCategory) {
        // If this errors we can just let it throw and the query will stop executing and return an error
        const response = await axios.post<{ name: string }, AxiosResponse<{ category: { id: number } }>>('/category', {
            name: newCategory
        })

        const newCategoryId = response.data.category.id

        body.category_id = newCategoryId.toString()
    }

    if (existingCategoryId) {
        body.category_id = existingCategoryId
    }

    if (quantityUnitId) {
        body.quantity_unit_id = quantityUnitId
    }

    return axios.post(`/recipe/${recipeId}/add-item`, body)
}

export function useAddItemToRecipeMutation() {
    return useMutation({
        mutationFn: addItemToRecipe,
        onMutate: async (payload) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            queryClient.cancelQueries(singleRecipeQueryKey(payload.recipeId))

            // Snapshot the data of the current queries
            type SingleRecipeQueryData = Awaited<ReturnType<typeof getSingleRecipe>> | undefined
            const singleRecipeQueryData: SingleRecipeQueryData = queryClient.getQueryData(singleRecipeQueryKey(payload.recipeId))

            type CategoriesQueryData = Awaited<ReturnType<typeof getCategories>> | undefined
            const categoriesQueryData: CategoriesQueryData = queryClient.getQueryData(categoriesQueryKey())

            type ItemsQueryData = Awaited<ReturnType<typeof getItems>> | undefined
            const itemsQueryData: ItemsQueryData = queryClient.getQueryData(itemsQueryKey())

            type QuantityUnitsData = Awaited<ReturnType<typeof getQuantityUnits>> | undefined
            const quantityUnitsQueryData: QuantityUnitsData = queryClient.getQueryData(quantityUnitsQueryKey())

            // Optimistically update to new value
            queryClient.setQueryData(singleRecipeQueryKey(payload.recipeId), (old: SingleRecipeQueryData) => {
                if (!old) return undefined

                const getAddedItemCategory = () => {
                    function getCategoryId() {
                        // If its a new item and therefore it is getting assigned a category on creation
                        if (payload.existingCategoryId) return Number(payload.existingCategoryId)

                        // If its an existing item, check the items list to see what it's category is (if it has a category)
                        const matchingItemCategory = itemsQueryData?.data.items.find(({ name }) => name === payload.itemName)?.category

                        return matchingItemCategory?.id || null
                    }

                    const categoryId = getCategoryId()

                    if (!categoryId) return null
                    return {
                        id: categoryId,
                        name: categoriesQueryData?.data.categories.find(({ id }) => id === categoryId)?.name || ''
                    }
                }

                function getAddedItemQuantity(): RecipeItem['item_quantity'] {
                    return {
                        quantity: payload.quantity,
                        quantity_unit: payload.quantityUnitId
                            ? quantityUnitsQueryData?.data.quantity_units.find((quantityUnit) => quantityUnit.id === payload.quantityUnitId) || null
                            : null
                    }
                }

                const newItems: RecipeItem[] = [
                    ...old.data.recipe.items,
                    {
                        id: 0,
                        recipe_item_id: 0,
                        name: payload.itemName,
                        category: getAddedItemCategory(),
                        item_quantity: getAddedItemQuantity(),
                        item_group: null,
                        sort_order: null
                    }
                ]

                const newData: SingleRecipeQueryData = {
                    data: {
                        recipe: {
                            ...old.data.recipe,
                            items: newItems.sort((a, b) => (a.name > b.name ? 1 : -1))
                        }
                    },
                    message: old.message
                }

                return newData
            })

            // Return context object with the current data
            return {
                singleListQueryData: singleRecipeQueryData
            }
        },
        onSuccess: (_, variables) => {
            // Invalidate affected queries on success
            queryClient.invalidateQueries(singleRecipeQueryKey(variables.recipeId))
            queryClient.invalidateQueries(itemsQueryKey())
        },
        onError: (err, variables, context) => {
            // Roll back to old data on error
            queryClient.setQueryData(singleRecipeQueryKey(variables.recipeId), context?.singleListQueryData)
            fireErrorNotification(err)
        }
    })
}

/***** Remove item from recipe *****/
const removeItemFromRecipe = ({ recipeId, recipeItemId }: { recipeId: string; recipeItemId: number }): Promise<MutationResponse> =>
    axios.post(`/recipe/${recipeId}/remove-item`, { recipe_item_id: recipeItemId })

export function useRemoveItemFromRecipeMutation() {
    return useMutation({
        mutationFn: removeItemFromRecipe,
        onMutate: (payload) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            queryClient.cancelQueries(singleRecipeQueryKey(payload.recipeId))

            // Snapshot the data of the current queries
            type SingleRecipeQueryData = Awaited<ReturnType<typeof getSingleRecipe>> | undefined
            const singleRecipeQueryData: SingleRecipeQueryData = queryClient.getQueryData(singleRecipeQueryKey(payload.recipeId))

            // Optimistically update to new value
            queryClient.setQueryData(singleRecipeQueryKey(payload.recipeId), (old: SingleRecipeQueryData) => {
                if (!old) return undefined

                const newData: SingleRecipeQueryData = {
                    data: {
                        recipe: {
                            ...old.data.recipe,
                            items: old.data.recipe.items.filter((item) => item.recipe_item_id !== payload.recipeItemId)
                        }
                    },
                    message: old.message
                }

                return newData
            })

            // Return context object with the current data
            return {
                singleListQueryData: singleRecipeQueryData
            }
        },
        onSuccess: (_, variables) => {
            // "isMutating" seems to return 1 for the current mutation even in the on success handler. If "isMutating" is greater than 1, that means that previous deletions are still happening. So we only want to invalidate the cache if its the last deletion
            if (queryClient.isMutating() < 2) {
                queryClient.invalidateQueries(singleRecipeQueryKey(variables.recipeId))
            }
        },
        onError: (err, variables, context) => {
            queryClient.setQueryData(singleRecipeQueryKey(variables.recipeId), context?.singleListQueryData)
            fireErrorNotification(err)
        }
    })
}

/***** Edit recipe *****/
const editRecipe = ({ recipeId, attributes }: { recipeId: string; attributes: EditRecipePayload }): Promise<MutationResponse> =>
    axios.put(`/recipe/${recipeId}`, attributes)

export function useEditRecipeMutation() {
    return useMutation({
        mutationFn: editRecipe
    })
}

/***** Duplicate recipe *****/
const duplicateRecipe = ({
    recipeId,
    attributes
}: {
    recipeId: string
    attributes: { name: string }
}): Promise<MutationResponse<{ new_recipe_id: number }>> => axios.post(`/recipe/${recipeId}/duplicate`, attributes)

export function useDuplicateRecipeMutation() {
    return useMutation({
        mutationFn: duplicateRecipe
    })
}

/***** Update item quantity *****/
const updateRecipeItemQuantity = ({
    recipeId,
    attributes
}: {
    recipeId: string
    attributes: UpdateRecipeItemQuantityPayload
}): Promise<MutationResponse> => axios.put(`/recipe/${recipeId}/update-item-quantity`, attributes)

export function useUpdateRecipeItemQuantityMutation() {
    return useMutation({
        mutationFn: updateRecipeItemQuantity,
        onSuccess(_, variables) {
            // queryClient.setQueryData(
            //     singleRecipeQueryKey(variables.recipeId || ''),
            //     (old: Awaited<ReturnType<typeof getSingleRecipe>> | undefined) => {
            //         if (!old) return undefined

            //         function getNewQuantityUnit() {
            //             const sentQuanityUnitId = variables.attributes.quantity_unit_id

            //             if (!variables.attributes.quantity_unit_id) {
            //                 return null
            //             }

            //             type QuantityUnitsData = Awaited<ReturnType<typeof getQuantityUnits>> | undefined
            //             const quantityUnitsQueryData: QuantityUnitsData = queryClient.getQueryData(quantityUnitsQueryKey())

            //             const foundUnit = quantityUnitsQueryData?.data.quantity_units.find((unit) => unit.id === sentQuanityUnitId)

            //             if (!foundUnit?.name || !foundUnit?.symbol) {
            //                 return null
            //             }

            //             const newUnit = {
            //                 id: sentQuanityUnitId,
            //                 name: foundUnit?.name,
            //                 symbol: foundUnit?.symbol
            //             } as QuantityUnit

            //             return newUnit
            //         }

            //         const newData = {
            //             ...old,
            //             data: {
            //                 recipe: {
            //                     ...old.data.recipe,
            //                     items: old.data.recipe.items.map((item) => {
            //                         if (item.recipe_item_id !== Number(variables.attributes.recipe_item_id)) {
            //                             return item
            //                         }

            //                         return {
            //                             ...item,
            //                             item_quantity: {
            //                                 quantity: Number(variables.attributes.quantity),
            //                                 quantity_unit: getNewQuantityUnit()
            //                             },
            //                             item_group: variables.attributes.item_group
            //                         }
            //                     })
            //                 }
            //             }
            //         }

            //         return newData
            //     }
            // )

            queryClient.invalidateQueries(singleRecipeQueryKey(variables.recipeId || ''))
        }
    })
}

/***** Sort recipe ingredients *****/
export function useSortRecipeIngredientsMutation() {
    const sortRecipeIngredients = ({
        recipeId,
        attributes
    }: {
        recipeId: string
        attributes: SortRecipeIngredientsPayload
    }): Promise<MutationResponse> => {
        return axios.put(`/recipe/${recipeId}/sort-items`, attributes)
    }

    return useMutation({
        mutationFn: sortRecipeIngredients,
        onMutate: (payload) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            queryClient.cancelQueries({ queryKey: singleRecipeQueryKey(payload.recipeId) })

            // Snapshot the data of the current queries
            type SingleRecipeQueryData = Awaited<ReturnType<typeof getSingleRecipe>> | undefined
            const singleRecipeQueryData: SingleRecipeQueryData = queryClient.getQueryData(singleRecipeQueryKey(payload.recipeId))

            // Optimistically update to new value
            queryClient.setQueryData(singleRecipeQueryKey(payload.recipeId || ''), (old: Awaited<ReturnType<typeof getSingleRecipe>> | undefined) => {
                if (!old) {
                    return undefined
                }

                const newData = {
                    ...old,
                    data: {
                        recipe: {
                            ...old.data.recipe,
                            items: old.data.recipe.items.map((item) => {
                                const matchingPayloadItem = payload.attributes.sort_orders.find(
                                    (payloadItem) => payloadItem.recipe_item_id === item.recipe_item_id
                                )

                                return {
                                    ...item,
                                    sort_order:
                                        matchingPayloadItem && Object.hasOwn(matchingPayloadItem ?? {}, 'sort_order')
                                            ? matchingPayloadItem?.sort_order
                                            : item.sort_order,
                                    item_group:
                                        matchingPayloadItem && Object.hasOwn(matchingPayloadItem ?? {}, 'ingredient_group')
                                            ? matchingPayloadItem.ingredient_group ?? null
                                            : item.item_group
                                }
                            })
                        }
                    }
                }

                return newData
            })

            // Return context object with the current data
            return {
                singleListQueryData: singleRecipeQueryData
            }
        },
        onSuccess: (_, variables) => {
            // "isMutating" seems to return 1 for the current mutation even in the on success handler. If "isMutating" is greater than 1, that means that previous deletions are still happening. So we only want to invalidate the cache if its the last deletion
            if (queryClient.isMutating() < 2) {
                queryClient.invalidateQueries({ queryKey: singleRecipeQueryKey(variables.recipeId) })
            }
        },
        onError: (_, variables, context) => {
            queryClient.setQueryData(singleRecipeQueryKey(variables.recipeId), context?.singleListQueryData)
            // fireErrorNotification(err)
        }
    })
}

/***** Accept shared recipe *****/
const createShareRecipeRequest = ({ recipeId, email }: { recipeId: number; email: string }): Promise<MutationResponse> =>
    axios.post(`/recipe/${recipeId}/create-share-request`, { email })

export function useCreateShareRecipeRequestMutation() {
    return useMutation({
        mutationFn: createShareRecipeRequest
    })
}

/***** Accept shared recipe *****/
const acceptSharedRecipe = ({
    newRecipeName,
    shareRequestId
}: {
    newRecipeName: string
    shareRequestId: number
}): Promise<MutationResponse<{ new_recipe_id: number }>> => axios.post(`/recipe/accept-share-request/${shareRequestId}`, { name: newRecipeName })

export function useAcceptSharedRecipeMutation() {
    return useMutation({
        mutationFn: acceptSharedRecipe
    })
}

/***** Dismiss shared recipe *****/
const dismissSharedRecipe = (shareRequestId: number): Promise<MutationResponse> => axios.put(`/recipe/dismiss-share-request/${shareRequestId}`)

export function useDismissSharedRecipeMutation() {
    return useMutation({
        mutationFn: dismissSharedRecipe
    })
}

/***** Upload recipe image *****/
const uploadRecipeImage = ({ id, formData }: { id: string; formData: FormData }): Promise<MutationResponse> =>
    axios.post(`/recipe/${id}/upload-image`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })

export function useUploadRecipeImageMutation() {
    return useMutation({
        mutationFn: uploadRecipeImage
    })
}

/***** Remove recipe image *****/
const removeRecipeImage = (id: string): Promise<MutationResponse> => axios.delete(`/recipe/${id}/remove-image`)

export function useRemoveRecipeImageMutation() {
    return useMutation({
        mutationFn: removeRecipeImage
    })
}

/***** Import recipe from image *****/
const importRecipeFromImage = (formData: FormData): Promise<MutationResponse<{ imported_recipe: string[] }>> =>
    axios.post('/recipe/import-from-image', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })

export function useImportRecipeFromImageMutation() {
    return useMutation({
        mutationFn: importRecipeFromImage
    })
}

/***** Confirm imported recipe *****/
const confirmImportedRecipe = ({
    newRecipeData,
    importedRecipeId
}: {
    newRecipeData: ConfirmImportedRecipePayload
    importedRecipeId: number
}): Promise<MutationResponse<{ new_recipe_id: number }>> =>
    axios.post(`/recipe/confirm-import-from-chrome-extension/${importedRecipeId}`, newRecipeData)

export function useConfirmImportedRecipeMutation() {
    return useMutation({
        mutationFn: confirmImportedRecipe
    })
}
